import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Alert, Card, PageHeader, Spinner, useToast } from '@platform/ui';

import { useEhrDirectory } from '../../features/scheduling/ehr-link-field';
import { ProvidersList } from '../../features/scheduling/providers-list';
import { useSchedulingClient } from '../../lib/api/scheduling-client-context';
import { describeApiError } from '../../lib/session/describe-error';

export const Route = createFileRoute('/providers/')({
  component: ProvidersPage,
});

/**
 * `/providers` — the provider list (MASTER_PLAN M4.11 over M4.1, `Docs/API.md` §2.7).
 *
 * The route is the only thing here that knows about the network: it fetches, it mutates,
 * and it hands `<ProvidersList>` plain data and callbacks (rules §17.1, §17.8).
 */
function ProvidersPage() {
  const client = useSchedulingClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [createError, setCreateError] = useState<string | undefined>(undefined);

  const ehrDirectory = useEhrDirectory();
  const providers = useQuery({ queryKey: ['providers'], queryFn: () => client.listProviders() });
  const types = useQuery({
    queryKey: ['appointment-types'],
    queryFn: () => client.listAppointmentTypes(),
  });
  const settings = useQuery({
    queryKey: ['scheduling-settings'],
    queryFn: () => client.getSettings(),
  });

  const create = useMutation({
    mutationFn: (input: {
      displayName: string;
      credentials: string;
      statesLicensed: string[];
      appointmentTypeIds: string[];
    }) => client.createProvider(input),
    onSuccess: async (provider) => {
      setCreateError(undefined);
      toast({ tone: 'success', message: `${provider.displayName} added.` });
      await queryClient.invalidateQueries({ queryKey: ['providers'] });
      await queryClient.invalidateQueries({ queryKey: ['appointment-types'] });
    },
    onError: (error: unknown) => {
      setCreateError(describeApiError(error));
    },
  });

  if (providers.isPending || types.isPending || settings.isPending) {
    return (
      <>
        <PageHeader title="Providers" />
        <Card className="p-6">
          <Spinner label="Loading providers" />
        </Card>
      </>
    );
  }

  if (providers.isError || types.isError || settings.isError) {
    return (
      <>
        <PageHeader title="Providers" />
        <Alert tone="error">The providers could not be loaded. Try again in a moment.</Alert>
      </>
    );
  }

  return (
    <ProvidersList
      providers={providers.data}
      appointmentTypes={types.data}
      statesServed={settings.data.statesServed}
      createError={createError}
      ehrDirectory={ehrDirectory}
      onCreate={(input) => {
        create.mutate(input);
      }}
      renderLink={(provider, children) => (
        <Link
          to="/providers/$providerId"
          params={{ providerId: provider.id }}
          className="font-medium text-primary-accessible hover:underline"
        >
          {children}
        </Link>
      )}
    />
  );
}
